/**
 * RenameSessionDialog - Dialog for renaming a session
 */

import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { useDeferredAutoFocus } from '@/hooks/useDeferredAutoFocus'
import { useUpdateSession } from '@/hooks/useSessions'

interface RenameSessionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  sessionId: string
  currentTitle: string
}

export function RenameSessionDialog({
  open,
  onOpenChange,
  sessionId,
  currentTitle,
}: RenameSessionDialogProps) {
  const { t } = useTranslation()
  const [title, setTitle] = useState(currentTitle)
  const updateSession = useUpdateSession()
  // Defer focus until the dialog's open animation settles so the iOS keyboard
  // doesn't rise mid-animation and shove the still-centering dialog around.
  const { ref: inputRef, onOpenAutoFocus } = useDeferredAutoFocus<HTMLInputElement>()

  // Reset title when dialog opens
  useEffect(() => {
    if (open) {
      setTitle(currentTitle)
    }
  }, [open, currentTitle])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) return

    try {
      await updateSession.mutateAsync({ id: sessionId, title: title.trim() })
      onOpenChange(false)
    } catch {
      // Error handled by mutation
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]" onOpenAutoFocus={onOpenAutoFocus}>
        <DialogHeader>
          <DialogTitle>{t('session.renameTitle')}</DialogTitle>
          <DialogDescription className="sr-only">{t('session.renameTitle')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <Input
            ref={inputRef}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('session.renamePlaceholder')}
            maxLength={100}
            className="mb-4"
          />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={!title.trim() || updateSession.isPending}
            >
              {updateSession.isPending ? (
                <Spinner className="h-4 w-4 mr-2" />
              ) : null}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
